'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function MemeyeDolguUygulamasiPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/memeye-dolgu-uygulamasi-dogal-dolgunluk-ve-sekillendirme';
  const pageTitle = 'Memeye Dolgu Uygulaması: Doğal Dolgunluk ve Şekillendirme';
  const pageDescription = 'Memeye dolgu uygulaması nedir? Yağ transferi ve hyaluronik asit dolgularla ameliyatsız meme büyütme nasıl yapılır, ne kadar kalıcıdır?';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-rose-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Memeye Dolgu Uygulaması', href: '/tr/blog/memeye-dolgu-uygulamasi-dogal-dolgunluk-ve-sekillendirme' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Memeye dolgu uygulaması ve doğal dolgunluk sağlama"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-pink-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Ameliyatsız Estetik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Memeye Dolgu Uygulaması: Doğal Dolgunluk ve Şekillendirme
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>8 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Meme estetiği yalnızca beden formunu değil, kişinin özgüvenini de doğrudan etkiler. Ancak birçok kadın, cerrahi implant operasyonlarına alternatif olarak daha doğal, kısa süreli ve geri dönüşümlü çözümler aramaktadır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu noktada meme dolgusu, yani ameliyatsız meme şekillendirme işlemi, son yıllarda popüler hale gelmiştir. Dolgu sayesinde meme dokusu kesilmeden, kısa sürede hacim kazandırılabilir, asimetriler düzeltilebilir ve kontur daha estetik hale getirilebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Meme Dolgusu Nedir?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Meme dolgusu, meme dokusu altına hyaluronik asit bazlı veya otolog yağ (lipofilling) dolgu maddesinin enjekte edilmesiyle yapılan ameliyatsız büyütme ve şekillendirme işlemidir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Amaç;
              </p>

              <div className="bg-pink-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-pink-800 space-y-2">
                  <li>Doğal bir dolgunluk sağlamak</li>
                  <li>Küçük asimetrileri düzeltmek</li>
                  <li>Cerrahiye gerek kalmadan orantılı görünüm kazandırmaktır.</li>
                </ul>
                <p className="text-pink-800 mt-3 font-medium">
                  💎 Cerrahi kesi yok, iz yok, dikiş yok.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Kullanılan Dolgu Türleri
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu Türü
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        İçerik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Kalıcılık Süresi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Hyaluronik Asit Dolgusu</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Vücuda uyumlu jel</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kontrollü hacim artışı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Yağ Transferi (Lipofilling)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kişinin kendi yağı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kalıcı sonuçlar, doğal doku</td>
                      <td className="px-6 py-4 text-sm text-gray-900">3–5 yıl</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kombine Teknik</td>
                      <td className="px-6 py-4 text-sm text-gray-900">HA + Yağ</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim + doğal görünüm</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–3 yıl</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Her dolgu tipi, hastanın vücut yapısı, doku kalınlığı ve beklentisine göre seçilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Uygulama Nasıl Yapılır?
              </h2>
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ol className="list-decimal list-inside text-blue-800 space-y-2">
                  <li>Lokal anestezi veya sedasyon altında gerçekleştirilir.</li>
                  <li>İnce kanüllerle meme dokusu altına dolgu enjekte edilir.</li>
                  <li>İşlem süresi ortalama 30–45 dakika'dır.</li>
                  <li>Hasta aynı gün taburcu edilir.</li>
                </ol>
                <p className="text-blue-800 mt-3">
                  İyileşme süreci hızlıdır; birkaç gün hafif ödem dışında ciddi yan etki görülmez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Kimler İçin Uygundur?
              </h2>
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 font-medium mb-3">✅ Uygundur:</p>
                <ul className="list-disc list-inside text-green-800 space-y-1">
                  <li>Cerrahi istemeyen hastalar</li>
                  <li>Meme asimetrisi bulunan kişiler</li>
                  <li>Doğum veya kilo kaybı sonrası hacim kaybı yaşayanlar</li>
                  <li>Küçük dolgunluk artışı isteyenler</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <p className="text-red-800 font-medium mb-3">⚠️ Uygun değildir:</p>
                <ul className="list-disc list-inside text-red-800 space-y-1">
                  <li>Gebe ve emziren kadınlar</li>
                  <li>Meme dokusunda kitle veya enfeksiyonu olanlar</li>
                  <li>200 ml üzeri büyütme isteyen hastalar (cerrahi gerekir)</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Avantajları
              </h2>
              <div className="bg-purple-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-purple-800 space-y-2">
                  <li>✅ Ameliyatsız, izsiz, hızlı iyileşme</li>
                  <li>✅ İstenirse tamamen geri alınabilir (hyaluronidaz ile)</li>
                  <li>✅ Doğal yumuşaklık hissi</li>
                  <li>✅ Simetri ve kontur düzeltme imkânı</li>
                  <li>✅ Günlük yaşama hızlı dönüş</li>
                </ul>
                <p className="text-purple-800 mt-3 font-medium">
                  💬 Yani, dolgu ile estetik değil, özgüven yenilenir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Meme Dolgusu ve İmplant Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-6">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Meme Dolgusu
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Silikon İmplant
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Uygulama</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Enjeksiyon (ameliyatsız)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi operasyon</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İyileşme</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–2 gün</td>
                      <td className="px-6 py-4 text-sm text-gray-900">2–4 hafta</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">1–3 yıl (dolgu tipine göre)</td>
                      <td className="px-6 py-4 text-sm text-gray-900">10–15 yıl</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Doğallık</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yumuşak ve doğal doku</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Daha belirgin hacim</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Risk</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Düşük</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cerrahi riskler mevcut</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Meme dolgusu, ameliyat olmak istemeyen ancak hafif ve doğal dolgunluk isteyen hastalar için ideal bir seçenektir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. İşlem Sonrası Dikkat Edilmesi Gerekenler
              </h2>
              <div className="bg-orange-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-orange-800 space-y-2">
                  <li>İlk 24 saat sırtüstü yatmak önerilir.</li>
                  <li>3 gün boyunca sıcak duş ve sauna yasaktır.</li>
                  <li>1 hafta ağır spor ve masajdan kaçınılmalıdır.</li>
                  <li>Dolgu yapılan bölgeye baskı uygulanmamalıdır.</li>
                </ul>
                <p className="text-orange-800 mt-3 font-medium">
                  Etkiler 1–2 hafta içinde tamamen oturur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Kalıcılık ve Tekrar Seanslar
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hyaluronik asit dolgular ortalama 12–18 ay, yağ transferi ise 3–5 yıl kalıcılığa sahiptir.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Düzenli bakım seanslarıyla dolgu yenilenebilir. Kişinin metabolizması, yaşam tarzı ve hormon dengesi kalıcılığı etkiler.
              </p>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Memeye dolgu uygulaması ve doğal dolgunluk sağlama"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Meme dolgusu kalıcı mıdır?</h3>
                  <p className="text-gray-700">Hyaluronik asit dolgular geçicidir, yağ transferi kalıcı sonuç verir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Emzirme sonrası yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet, ancak emzirme süreci tamamen bittikten sonra uygulanmalıdır.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Dolgu meme kanseri riskini artırır mı?</h3>
                  <p className="text-gray-700">Hayır. Kullanılan maddeler biyouyumlu olup kanser riskiyle ilişkili değildir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. MR veya mamografi çekiminde sorun olur mu?</h3>
                  <p className="text-gray-700">Hayır, hyaluronik asit dolgular MR'da belirgin değildir ve radyolojik incelemeyi engellemez.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Dolgu sonrası masaj yapılmalı mı?</h3>
                  <p className="text-gray-700">Hayır. Özellikle ilk 1 hafta masaj önerilmez, dolgu yerinden oynayabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society of Plastic Surgeons (ASPS) – "Hyaluronic Acid Breast Fillers"</li>
                <li>Türk Plastik, Rekonstrüktif ve Estetik Cerrahi Derneği (TPRECD) – "Meme Dolgusu ve Yağ Transferi Uygulama Rehberi"</li>
                <li>Mayo Clinic (2024) – "Non-Surgical Breast Augmentation Techniques"</li>
                <li>Harvard Health Publishing – "Comparing Implants and Injectable Fillers for Breast Shaping"</li>
                <li>ISAPS Global Report 2024 – "Trends in Minimally Invasive Breast Enhancement"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#memeestetiği</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#yağtransferi</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm">#göğüsşekillendirme</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-pink-600 to-rose-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Meme Estetiği Hakkında Bilgi Alın</h3>
              <p className="mb-6">Ameliyatsız meme şekillendirme ve dolgu uygulamaları hakkında uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Meme dolgu uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-pink-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu işlemlerinin yüz liftingi etkisi yaratıp yaratmadığı ve nasıl uygulandığı.</p>
            </Link>
            <Link
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe işlemi, hangi içerikler kullanılır ve nasıl uygulanır?</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
